/**
 * Turtle Graphics Service
 * Python-style turtle graphics for JavaScript
 * Allows students to create art through code
 */

class Turtle {
  constructor(canvas, ctx) {
    this.canvas = canvas;
    this.ctx = ctx;
    this.x = canvas.width / 2;
    this.y = canvas.height / 2;
    this.angle = 0; // 0 = right, 90 = up, 180 = left, 270 = down
    this.penDown = true;
    this.penColor = '#000000';
    this.penWidth = 2;
    this.speed = 5;
    this.history = [];
    this.animationFrame = null;
  }

  // Movement commands
  forward(distance) {
    const radians = (this.angle * Math.PI) / 180;
    const newX = this.x + distance * Math.cos(radians);
    const newY = this.y - distance * Math.sin(radians); // Canvas Y is inverted

    if (this.penDown) {
      this.ctx.beginPath();
      this.ctx.moveTo(this.x, this.y);
      this.ctx.lineTo(newX, newY);
      this.ctx.strokeStyle = this.penColor;
      this.ctx.lineWidth = this.penWidth;
      this.ctx.lineCap = 'round';
      this.ctx.stroke();
    }

    this.x = newX;
    this.y = newY;
    
    this.history.push({
      command: 'forward',
      distance,
      from: { x: this.x - distance * Math.cos(radians), y: this.y + distance * Math.sin(radians) },
      to: { x: this.x, y: this.y }
    });
  }

  backward(distance) {
    this.forward(-distance);
  }

  // Rotation commands
  right(angle) {
    this.angle = (this.angle - angle) % 360;
    if (this.angle < 0) this.angle += 360;
    this.history.push({ command: 'right', angle });
  }

  left(angle) {
    this.angle = (this.angle + angle) % 360;
    this.history.push({ command: 'left', angle });
  }

  // Pen control
  penup() {
    this.penDown = false;
    this.history.push({ command: 'penup' });
  }

  pendown() {
    this.penDown = true;
    this.history.push({ command: 'pendown' });
  }

  // Appearance
  color(c) {
    this.penColor = c;
    this.history.push({ command: 'color', color: c });
  }

  width(w) {
    this.penWidth = w;
    this.history.push({ command: 'width', width: w });
  }

  // Position
  goto(x, y) {
    // Center coordinates (canvas center is 0,0 in turtle coords)
    const canvasX = this.canvas.width / 2 + x;
    const canvasY = this.canvas.height / 2 - y;

    if (this.penDown) {
      this.ctx.beginPath();
      this.ctx.moveTo(this.x, this.y);
      this.ctx.lineTo(canvasX, canvasY);
      this.ctx.strokeStyle = this.penColor;
      this.ctx.lineWidth = this.penWidth;
      this.ctx.lineCap = 'round';
      this.ctx.stroke();
    }

    this.x = canvasX;
    this.y = canvasY;
    this.history.push({ command: 'goto', x, y });
  }

  setposition(x, y) {
    this.goto(x, y);
  }

  home() {
    this.goto(0, 0);
    this.angle = 0;
  }

  // Drawing shapes
  circle(radius, extent = 360) {
    const steps = Math.abs(Math.floor(extent / 5));
    const angleStep = extent / steps;
    const circumference = 2 * Math.PI * radius;
    const stepDistance = (circumference * extent / 360) / steps;

    for (let i = 0; i < steps; i++) {
      this.forward(stepDistance);
      this.left(angleStep);
    }
  }

  dot(size = 5) {
    this.ctx.beginPath();
    this.ctx.arc(this.x, this.y, size / 2, 0, 2 * Math.PI);
    this.ctx.fillStyle = this.penColor;
    this.ctx.fill();
    this.history.push({ command: 'dot', size });
  }

  // State
  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.history = [];
  }

  reset() {
    this.clear();
    this.home();
    this.penDown = true;
    this.penColor = '#000000';
    this.penWidth = 2;
    this.angle = 0;
  }

  // Get current state
  position() {
    return {
      x: this.x - this.canvas.width / 2,
      y: this.canvas.height / 2 - this.y
    };
  }

  heading() {
    return this.angle;
  }
}

// Code executor with safety limits
class TurtleExecutor {
  constructor(canvas, ctx) {
    this.turtle = new Turtle(canvas, ctx);
    this.maxOperations = 10000; // Prevent infinite loops
    this.operationCount = 0;
  }

  executeCode(code) {
    this.operationCount = 0;
    this.turtle.reset();

    try {
      // Create a safe execution environment
      const safeCode = this.wrapCode(code);
      
      // Execute in try-catch
      // eslint-disable-next-line no-new-func
      const func = new Function('turtle', safeCode);
      func(this.createTurtleAPI());

      return {
        success: true,
        operations: this.operationCount
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        line: this.extractLineNumber(error)
      };
    }
  }

  wrapCode(code) {
    // Add operation counter to loops
    return code;
  }

  createTurtleAPI() {
    const self = this;
    
    const checkLimit = () => {
      self.operationCount++;
      if (self.operationCount > self.maxOperations) {
        throw new Error('Too many operations! Check for infinite loops.');
      }
    };

    return {
      forward: (d) => { checkLimit(); self.turtle.forward(d); },
      backward: (d) => { checkLimit(); self.turtle.backward(d); },
      right: (a) => { checkLimit(); self.turtle.right(a); },
      left: (a) => { checkLimit(); self.turtle.left(a); },
      penup: () => { checkLimit(); self.turtle.penup(); },
      pendown: () => { checkLimit(); self.turtle.pendown(); },
      color: (c) => { checkLimit(); self.turtle.color(c); },
      width: (w) => { checkLimit(); self.turtle.width(w); },
      goto: (x, y) => { checkLimit(); self.turtle.goto(x, y); },
      setposition: (x, y) => { checkLimit(); self.turtle.setposition(x, y); },
      home: () => { checkLimit(); self.turtle.home(); },
      circle: (r, e) => { checkLimit(); self.turtle.circle(r, e); },
      dot: (s) => { checkLimit(); self.turtle.dot(s); },
      clear: () => { self.turtle.clear(); },
      position: () => self.turtle.position(),
      heading: () => self.turtle.heading(),
      
      // Aliases
      fd: (d) => { checkLimit(); self.turtle.forward(d); },
      bk: (d) => { checkLimit(); self.turtle.backward(d); },
      rt: (a) => { checkLimit(); self.turtle.right(a); },
      lt: (a) => { checkLimit(); self.turtle.left(a); },
      pu: () => { checkLimit(); self.turtle.penup(); },
      pd: () => { checkLimit(); self.turtle.pendown(); }
    };
  }

  extractLineNumber(error) {
    const match = error.stack?.match(/:(\d+):/);
    return match ? parseInt(match[1]) : null;
  }
}

export { Turtle, TurtleExecutor };
export default TurtleExecutor;
